/**
 * GroupSettingsStatusPanel — which cameras of a group have applied their
 * settings, which have fetched them and await confirmation (sent, item 396),
 * which are still pending, and which cannot be confirmed (item 226, task 14).
 * Read-only; the per-camera state comes from the same server code as the
 * camera sheet's "Not applied yet" / "Sent, awaiting confirmation" markers.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { classify, settingKeyOfPendingField, summarize, type SettingsStatusClass } from '@/lib/groupSettingsStatus'
import { formatSettingValue, settingLabel } from '@/lib/httpSettingsForm'
import { cn, formatRelativeTime } from '@/lib/utils'

const CLASS_LABELS: Record<SettingsStatusClass, string> = {
  applied: 'Applied',
  sent: 'Sent',
  pending: 'Pending',
  cant_confirm: "Can't confirm",
  none: 'No pending change',
  not_http: 'Not HTTP',
}

// One colour per state, the same as the camera card and sheet (#396): green =
// confirmed, blue = handed to the camera and waiting for its report, amber = not
// handed over yet, rose = silent too long, do not wait on it.
const CLASS_STYLES: Record<SettingsStatusClass, string> = {
  applied: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  sent: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  cant_confirm: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
  none: 'bg-muted text-muted-foreground border-border',
  not_http: 'bg-muted text-muted-foreground border-border',
}

interface Props {
  groupId: number
}

export function GroupSettingsStatusPanel({ groupId }: Props) {
  const [expanded, setExpanded] = useState(false)
  const status = useQuery({
    queryKey: queryKeys.cameraGroups.settingsStatus(groupId),
    queryFn: () => cameraGroupsApi.getSettingsStatus(groupId),
    ...queryOptions.cameraGroups.settingsStatus,
  })
  const now = Date.now()
  const cameras = status.data?.cameras ?? []
  const c = summarize(cameras, now)

  return (
    <section className="space-y-2 rounded-md border border-border px-3 py-2.5 text-sm">
      <div className="flex items-center gap-2">
        <h4 className="flex-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</h4>
        <Button size="sm" variant="ghost" onClick={() => status.refetch()} disabled={status.isFetching}
          aria-label="Refresh status">
          <RefreshCw className={cn('h-3.5 w-3.5', status.isFetching && 'animate-spin')} />
        </Button>
      </div>

      {status.isPending ? (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading status…
        </p>
      ) : status.isError ? (
        <p className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Could not load the settings status. You can still choose settings below.
        </p>
      ) : (
        <>
          <p>
            {`${c.applied} applied · ${c.sent} sent · ${c.pending} pending · ${c.cant_confirm} can't confirm · ${c.none} no pending change · ${c.not_http} not HTTP`}
          </p>
          {cameras.length > 0 && (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              aria-expanded={expanded}
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {expanded ? 'Hide cameras' : 'Show cameras'}
            </button>
          )}
          {expanded && (
            <ul className="max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {cameras.map((cam) => {
                const cls = classify(cam, now)
                return (
                  <li key={cam.camera_id} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2">
                    <span className="flex-1 min-w-[8rem] font-medium">{cam.name}</span>
                    <div className="flex-[2] min-w-[10rem] space-y-0.5">
                      {cam.pending_fields.map((f) => {
                        const key = settingKeyOfPendingField(f.field)
                        return (
                          <div key={f.field}>
                            {`${settingLabel(key)}: ${formatSettingValue(key, f.current)} → ${formatSettingValue(key, f.desired)}`}
                          </div>
                        )
                      })}
                    </div>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatRelativeTime(cam.params_reported_at)}
                    </span>
                    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium', CLASS_STYLES[cls])}>
                      {CLASS_LABELS[cls]}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
